import { usePageSEO } from "@/hooks/usePageSEO";
import EditorialHeader from "@/components/editorial/EditorialHeader";
import AcademyLaunchModal from "@/components/academy/AcademyLaunchModal";
import EditorialFooter from "@/components/editorial/EditorialFooter";
import EditorialGridLayout from "@/components/editorial/EditorialGridLayout";
import EditorialSectionDivider from "@/components/editorial/EditorialSectionDivider";
import GridLinesWrapper from "@/components/editorial/GridLinesWrapper";
import EditorialAcademySection from "@/components/editorial/EditorialAcademySection";
import { useTheme } from "@/contexts/ThemeContext";

import { Play, Clock, BookOpen, Award, CheckCircle2, Lock, Target, Share2, Mail, FileText, Zap, Bot, GraduationCap, Sparkles, Video } from "lucide-react";
import AnimatedBorderBadge from "@/components/editorial/AnimatedBorderBadge";

const AcademyContent = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Theme-aware colors - explicit for proper dark mode transitions
  const bgColor = isDark ? '#111111' : '#FFFFFF';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const cardBgColor = isDark ? '#1A1A1A' : '#FFFFFF';
  const borderColor = isDark ? '#333333' : '#E0E0E0';
  const featureCardBg = isDark ? '#1A1A1A' : '#F8F8F8';
  const iconBgColor = isDark ? '#222222' : '#F0F0F0';
  const iconColor = isDark ? '#AAAAAA' : '#555555';
  const placeholderBg = isDark ? '#222222' : '#F0F0F0';
  const progressBg = isDark ? '#333333' : '#E0E0E0';
  const progressFill = isDark ? '#FFFFFF' : '#111111';
  const metaColor = isDark ? '#888888' : '#666666';
  
  // V3 button styling - inverted: white on dark, black on light
  const ctaBtnBg = isDark ? '#FFFFFF' : '#111111';
  const ctaBtnText = isDark ? '#111111' : '#FFFFFF';
  const lockedBtnBg = isDark ? '#444444' : '#888888';

  const courses = [
    {
      id: 1,
      title: "Content Marketing Mastery",
      description: "Create compelling content that attracts, engages, and converts your ideal customers.",
      category: "content",
      duration: "2h",
      lessons: 12,
      level: "Beginner",
      image: "/lovable-uploads/comprehensive-strategy.png",
      completed: false,
      locked: false,
      progress: 0
    },
    {
      id: 2,
      title: "Social Media Strategy",
      description: "Build a powerful social presence and engage authentically with your audience across all platforms.",
      category: "social",
      duration: "1h 45min",
      lessons: 10,
      level: "Beginner",
      image: "/lovable-uploads/proven-performance.png",
      completed: false,
      locked: false,
      progress: 0
    },
    {
      id: 3,
      title: "Email Marketing Fundamentals",
      description: "Design high-converting email campaigns that nurture leads and drive sales on autopilot.",
      category: "email",
      duration: "1h 30min",
      lessons: 9,
      level: "Intermediate",
      image: "/lovable-uploads/honest-collaboration.png",
      completed: false,
      locked: false,
      progress: 0
    },
    {
      id: 4,
      title: "Inbound Marketing Strategy",
      description: "Attract qualified leads naturally through strategic content and SEO optimization.",
      category: "inbound",
      duration: "2h 15min",
      lessons: 14,
      level: "Intermediate",
      image: "/lovable-uploads/comprehensive-strategy.png",
      completed: false,
      locked: false,
      progress: 0
    },
    {
      id: 5,
      title: "Marketing Automation & Workflows",
      description: "Scale your marketing efforts with intelligent automation that works while you sleep.",
      category: "automation",
      duration: "1h 50min",
      lessons: 11,
      level: "Advanced",
      image: "/lovable-uploads/proven-performance.png",
      completed: false,
      locked: false,
      progress: 0
    },
    {
      id: 6,
      title: "AI-Powered Marketing",
      description: "Leverage AI agents and tools to 10x your marketing productivity and results.",
      category: "ai",
      duration: "2h",
      lessons: 13,
      level: "Advanced",
      image: "/lovable-uploads/honest-collaboration.png",
      completed: false,
      locked: false,
      progress: 0
    }
  ];

  const getLevelStyles = (level: string) => {
    switch(level) {
      case "Beginner": return { bg: '#10B98115', color: '#10B981' };
      case "Intermediate": return { bg: '#3B82F615', color: '#3B82F6' };
      case "Advanced": return { bg: '#8B5CF615', color: '#8B5CF6' };
      default: return { bg: '#88888815', color: '#888888' };
    }
  };

  return (
    <EditorialGridLayout>
      <AcademyLaunchModal />
      <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: bgColor }}>
        <EditorialHeader />
        
        {/* Hero Section */}
        <section className="pt-32 lg:pt-40 pb-16" style={{ backgroundColor: bgColor }}>
          <div className="max-w-[1232px] mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <AnimatedBorderBadge text="Academy" icon={Award} />
              
              <h1 
                className="text-3xl lg:text-4xl font-bold mb-4 leading-tight tracking-tight"
                style={{ color: headingColor }}
              >
                The Free Academy.
              </h1>
              
              <p className="text-sm mb-8" style={{ color: textColor }}>
                Master modern marketing with expert-led courses, step-by-step tutorials, and certification tracks—completely free.
              </p>

              <div className="flex flex-col md:flex-row items-center justify-center md:space-x-6 space-y-2 md:space-y-0 text-sm mb-8" style={{ color: textColor }}>
                <div className="flex items-center">
                  <Award className="h-4 w-4 mr-2" style={{ color: iconColor }} />
                  <span>100% Free & Online</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-4 w-4 mr-2" style={{ color: iconColor }} />
                  <span>Unlimited Access</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 mr-2" style={{ color: iconColor }} />
                  <span>Hundreds of Topics</span>
                </div>
              </div>

              {/* CTA Button - V3 style: text-xs font-semibold */}
              <span
                className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg opacity-60 cursor-not-allowed"
                style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
              >
                Launching Soon
              </span>
            </div>
          </div>
        </section>

        <EditorialSectionDivider className="my-2" />

        <div>
          <EditorialSectionDivider className="mb-4" />
          
          {/* Academy Animation Section */}
          <GridLinesWrapper>
            <EditorialAcademySection />
          </GridLinesWrapper>

          <EditorialSectionDivider />

          {/* Topic Cards Section */}
          <GridLinesWrapper>
            <section className="py-16 px-6">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <AnimatedBorderBadge text="Curriculum" icon={BookOpen} />
                  <h2 
                    className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
                    style={{ color: headingColor }}
                  >
                    What You'll Learn.
                  </h2>
                  <p className="text-sm" style={{ color: textColor }}>
                    Master every aspect of modern marketing with our comprehensive curriculum.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { icon: Target, title: "Inbound", desc: "Attract & convert leads naturally through compelling content and strategic engagement." },
                    { icon: Share2, title: "Social Media", desc: "Master multi-channel engagement and build authentic connections with your audience." },
                    { icon: Mail, title: "Email Marketing", desc: "Build high-converting campaigns with segmentation, automation, and personalization." },
                    { icon: FileText, title: "Content Marketing", desc: "Create compelling stories that resonate with your target audience and drive action." },
                    { icon: Zap, title: "Workflow Automation", desc: "Scale your operations with intelligent systems that automate repetitive tasks." },
                    { icon: Bot, title: "AI Agents", desc: "Deploy 24/7 digital employees that handle conversations, calls, and support automatically." }
                  ].map((topic, index) => {
                    const gridLineColor = isDark ? '#444444' : '#d0d0d0';
                    const iconBoxStyle: React.CSSProperties = {
                      backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
                      backgroundSize: '5px 5px',
                      WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                      maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
                    };
                    return (
                      <div 
                        key={index}
                        className="rounded-xl p-6 transition-all duration-300 hover:shadow-lg group"
                        style={{ 
                          backgroundColor: featureCardBg,
                          border: `1px solid ${borderColor}`
                        }}
                      >
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 mx-auto transition-transform group-hover:scale-110"
                          style={iconBoxStyle}
                        >
                          <topic.icon className="w-5 h-5" style={{ color: isDark ? '#888888' : '#666666' }} strokeWidth={1.5} />
                        </div>
                        <h3 
                          className="text-sm font-semibold mb-2 text-center"
                          style={{ color: headingColor }}
                        >
                          {topic.title}
                        </h3>
                        <p 
                          className="text-xs leading-relaxed text-center"
                          style={{ color: textColor }}
                        >
                          {topic.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </GridLinesWrapper>

          <EditorialSectionDivider />

          {/* Popular Courses Section */}
          <GridLinesWrapper>
            <section className="py-16 px-6">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <AnimatedBorderBadge text="Top Picks" icon={Sparkles} />
                  <h2 
                    className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
                    style={{ color: headingColor }}
                  >
                    Popular Courses.
                  </h2>
                  <p className="text-sm" style={{ color: textColor }}>
                    Start with our most popular courses to accelerate your learning.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {courses.map((course) => {
                    const levelStyle = getLevelStyles(course.level);
                    return (
                      <div
                        key={course.id}
                        className="group rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
                        style={{ 
                          backgroundColor: cardBgColor,
                          border: `1px solid ${borderColor}`
                        }}
                      >
                        {/* Course Image */}
                        <div className="relative h-40 overflow-hidden" style={{ backgroundColor: placeholderBg }}>
                          <img
                            src={course.image}
                            alt={course.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                          {course.locked && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <Lock className="w-6 h-6 text-white" />
                            </div>
                          )}
                          {course.progress > 0 && (
                            <div className="absolute bottom-0 left-0 right-0 h-1" style={{ backgroundColor: progressBg }}>
                              <div
                                className="h-full"
                                style={{ width: `${course.progress}%`, backgroundColor: progressFill }}
                              />
                            </div>
                          )}
                        </div>

                        {/* Course Content */}
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span 
                              className="text-[10px] font-medium px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: levelStyle.bg, color: levelStyle.color }}
                            >
                              {course.level}
                            </span>
                            {course.completed && (
                              <span 
                                className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1"
                                style={{ backgroundColor: '#10B98115', color: '#10B981' }}
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                Completed
                              </span>
                            )}
                          </div>

                          <h3 
                            className="text-sm font-semibold mb-2"
                            style={{ color: headingColor }}
                          >
                            {course.title}
                          </h3>
                          <p 
                            className="text-xs mb-4 line-clamp-2"
                            style={{ color: textColor }}
                          >
                            {course.description}
                          </p>

                          <div 
                            className="flex items-center justify-between text-[10px] mb-4"
                            style={{ color: metaColor }}
                          >
                            <div className="flex items-center gap-1">
                              <Play className="w-3 h-3" />
                              <span>{course.lessons} lessons</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{course.duration}</span>
                            </div>
                          </div>

                          <span
                            className="w-full py-2 text-xs font-semibold rounded-lg text-center block opacity-60 cursor-not-allowed"
                            style={{ 
                              backgroundColor: ctaBtnBg, 
                              color: ctaBtnText 
                            }}
                          >
                            Launching Soon
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </GridLinesWrapper>

          <EditorialSectionDivider />

          {/* Done-For-You Showcase Section */}
          <GridLinesWrapper className={isDark ? '' : 'bg-[#f4f2f1]'}>
            <section className="py-20">
              <div className="max-w-[1232px] mx-auto px-6">
                <div className="text-center mb-12">
                  <AnimatedBorderBadge text="Done-For-You" icon={Video} />
                  <h2 
                    className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-[1.1] tracking-tight"
                    style={{ color: headingColor }}
                  >
                    Outsource, Outperform.
                  </h2>
                </div>

                {/* Masonry Grid */}
                <div className="max-w-3xl mx-auto mb-12 px-4 md:px-6">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Left Column */}
                    <div className="space-y-3">
                      <div className="relative group overflow-hidden rounded-lg aspect-[9/16]">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Highlight%20Reel%20-%20Lotus.mp4" type="video/mp4" />
                        </video>
                      </div>
                      <div className="relative group overflow-hidden rounded-lg aspect-square">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/TXIAA%20(1).mp4" type="video/mp4" />
                        </video>
                      </div>
                      <div className="relative group overflow-hidden rounded-lg aspect-square">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Brown%20Simple%20Marketing%20Planner%20Product%20Mockup%20Instagram%20Post.mp4" type="video/mp4" />
                        </video>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-3">
                      <div className="relative group overflow-hidden rounded-lg aspect-square">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/1020%20%2810%29.mp4" type="video/mp4" />
                        </video>
                      </div>
                      <div className="relative group overflow-hidden rounded-lg aspect-square">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/TXIAA.mp4" type="video/mp4" />
                        </video>
                      </div>
                      <div className="relative group overflow-hidden rounded-lg aspect-[9/16]">
                        <video autoPlay muted loop playsInline className="w-full h-full object-cover">
                          <source src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/1020%20(6).mp4" type="video/mp4" />
                        </video>
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="text-center">
                  <button 
                    onClick={() => window.location.href = '/done-for-you'}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 hover:scale-[1.02] hover:shadow-md hover:opacity-90 active:scale-[0.98]"
                    style={{ 
                      backgroundColor: ctaBtnBg,
                      color: ctaBtnText
                    }}
                  >
                    We Grow Businesses
                  </button>
                  <p 
                    className="text-sm max-w-2xl mx-auto mt-6"
                    style={{ color: textColor }}
                  >
                    See how businesses like yours are telling their Story with us.
                  </p>
                </div>
              </div>
            </section>
          </GridLinesWrapper>

          <EditorialSectionDivider />

          {/* CTA Section */}
          <GridLinesWrapper>
            <section className="py-20 px-6">
              <div className="max-w-4xl mx-auto text-center">
                <AnimatedBorderBadge text="Certification" icon={GraduationCap} />
                <h2 
                  className="text-3xl lg:text-4xl font-bold tracking-tight mb-4"
                  style={{ color: headingColor }}
                >
                  Become a Certified Storyteller.
                </h2>
                <p 
                  className="text-sm mb-8 max-w-2xl mx-auto"
                  style={{ color: textColor }}
                >
                  Access all the expert-level knowledge and strategies needed to grow your business and enhance your career. 100% free, no credit card required.
                </p>
                <span
                  className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg opacity-60 cursor-not-allowed"
                  style={{ backgroundColor: ctaBtnBg, color: ctaBtnText }}
                >
                  Launching Soon
                </span>
              </div>
            </section>
          </GridLinesWrapper>

          <EditorialSectionDivider />
        </div>

        <EditorialFooter />

      </div>
    </EditorialGridLayout>
  );
};

const Academy = () => {
  usePageSEO({
    title: 'Free Marketing Academy | Story Marketing Group',
    description: 'Master modern marketing with expert-led courses, step-by-step tutorials, and certification tracks—completely free.',
    canonical: 'https://storymarketing.group/academy',
    keywords: 'marketing courses, free marketing education, marketing certification, online marketing training'
  });

  return <AcademyContent />;
};

export default Academy;